import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';
import AppointmentConflictModal from './AppointmentConflictModal';
import { confirmDiscardChanges } from '../../utils/unsavedChanges';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const STATUS_OPTIONS = [
  'Agendado',
  'Confirmado',
  'Realizado',
  'Cancelado',
  'Não Compareceu',
];


const isHHMM = (s) => /^\d{2}:\d{2}$/.test(s);
const isYMD = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s);

export default function AppointmentFormModal({
  open,
  onClose,
  initialData = null,
  patients = [],
  treatments = [],          // <- recebe lista do paciente (ou geral, na Agenda)
  professionals = [],
  currentPatient = null,    // { id, full_name } quando vem do prontuário
  toast,
  onSaved,
}) {
  // ===== Paciente =====
  const [patientId, setPatientId] = useState(currentPatient?.id || '');
  const [patientName, setPatientName] = useState(currentPatient?.full_name || '');
  const [patientSearch, setPatientSearch] = useState('');

  // Ordena pacientes
  const filteredPatients = useMemo(() => {
    const q = patientSearch.trim().toLowerCase();
    const list = Array.isArray(patients) ? patients : [];
    const sorted = [...list].sort((a, b) =>
      (a.full_name || '').localeCompare(b.full_name || '', 'pt-BR', { sensitivity: 'base' })
    );
    if (!q) return sorted;
    return sorted.filter((p) =>
      (p.full_name || '').toLowerCase().includes(q) ||
      (p.email || '').toLowerCase().includes(q) ||
      (p.phone || '').toLowerCase().includes(q)
    );
  }, [patients, patientSearch]);

  const activeProfessionals = useMemo(() => {
    const list = Array.isArray(professionals) ? professionals : [];
    return list.filter((p) => p?.id && p?.name && p?.is_active !== false);
  }, [professionals]);

  // ===== Form =====
  const [appointmentDate, setAppointmentDate] = useState('');
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('');
  const [procedure, setProcedure] = useState('');
  const [status, setStatus] = useState('Agendado');
  const [notes, setNotes] = useState('');
  const [professionalId, setProfessionalId] = useState('');
  const [professionalName, setProfessionalName] = useState('');

  // Unregistered patient & Settings
  const [isUnregistered, setIsUnregistered] = useState(false);
  const [patientPhone, setPatientPhone] = useState('');
  const [appointmentDuration, setAppointmentDuration] = useState(30);

  useEffect(() => {
    let mounted = true;
    const fetchSettings = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/clinic-settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (mounted && data?.appointment_duration) {
          setAppointmentDuration(data.appointment_duration);
        }
      } catch (err) {
        console.warn('Erro ao carregar configuracoes', err);
      }
    };
    if (open) fetchSettings();
    return () => { mounted = false; };
  }, [open]);

  const handleStartTimeChange = (val) => {
    setStartTime(val);
    if (isHHMM(val) && appointmentDuration) {
      const [h, m] = val.split(':').map(Number);
      const totalMins = h * 60 + m + appointmentDuration;
      const endH = Math.floor(totalMins / 60) % 24;
      const endM = totalMins % 60;
      const formattedEnd = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
      setEndTime(formattedEnd);
    }
  };

  // Tratamento + valor
  const [treatmentId, setTreatmentId] = useState('');
  const [amount, setAmount] = useState('');           // string para input
  const [amountTouched, setAmountTouched] = useState(false); // para sabermos se o usuário alterou manualmente
  const [pushToFinance, setPushToFinance] = useState(true);

  const [saving, setSaving] = useState(false);
  const [conflictInfo, setConflictInfo] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  const requestClose = () => {
    if (confirmDiscardChanges(isDirty)) onClose && onClose();
  };

  // Fecha com ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && open) requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, isDirty, onClose]);

  // Preenche para edição
  useEffect(() => {
    if (!open) return;
    if (initialData) {
      setPatientId(initialData.patient_id || currentPatient?.id || '');
      setPatientName(initialData.patient_name || currentPatient?.full_name || '');
      setPatientPhone(initialData.patient_phone || '');
      setIsUnregistered(!initialData.patient_id && !!initialData.patient_name);
      setAppointmentDate(initialData.appointment_date || '');
      setStartTime(initialData.start_time || '08:00');
      setEndTime(initialData.end_time || '');
      setProcedure(initialData.procedure || '');
      setStatus(initialData.status || 'Agendado');
      setNotes(initialData.notes || '');
      setProfessionalId(initialData.professional_id || '');
      setProfessionalName(initialData.professional_name || '');      
      setTreatmentId(initialData.treatment_id || '');
      setAmount(
        typeof initialData.amount === 'number'
          ? String(initialData.amount.toFixed(2))
          : (initialData.amount || '')
      );
      setPushToFinance(
        initialData.push_to_finance !== false
      );

      setAmountTouched(false);
    } else {
      setPatientId(currentPatient?.id || '');
      setPatientName(currentPatient?.full_name || '');
      setPatientPhone('');
      setIsUnregistered(false);
      setAppointmentDate('');
      setStartTime('08:00');
      setEndTime('');
      setProcedure('');
      setStatus('Agendado');
      setNotes('');
      const mainProf = activeProfessionals.find((p) => p.is_main) || activeProfessionals[0] || null;
      setProfessionalId(mainProf?.id || '');
      setProfessionalName(mainProf?.name || '');      
      setTreatmentId('');
      setAmount('');
      setPushToFinance(true);
      setAmountTouched(false);
    }
    setConflictInfo(null);
    setIsDirty(false);
  }, [open, initialData, currentPatient, activeProfessionals]);

  const onSelectPatient = (id) => {
      const p = patients.find((px) => px.id === id);

      setPatientId(p?.id || '');
      setPatientName(p?.full_name || '');

      setTreatmentId('');
      setAmount('');
      setAmountTouched(false);
  };

  // Tratamento selecionado + valor padrão

  const availableTreatments = useMemo(() => {
    if (!patientId) {
      return [];
    }

    return (treatments || []).filter(
      (treatment) =>
        treatment.patient_id === patientId &&
        treatment.is_inactive !== true
    );
  }, [treatments, patientId]);

  const selectedTreatment = useMemo(() => {
      return availableTreatments.find(
          (t) => t.id === treatmentId
      ) || null;
  }, [availableTreatments, treatmentId]);

  const selectedTreatmentPrice = useMemo(() => {
    if (!selectedTreatment) return null;
    const raw =
      selectedTreatment.cost ??
      selectedTreatment.price ??
      selectedTreatment.amount ??
      selectedTreatment.value ??
      null;
    if (raw == null) return null;
    const num = Number(raw);
    return Number.isFinite(num) ? num : null;
  }, [selectedTreatment]);

  // Quando trocar de tratamento, se o usuário NÃO mexeu no valor, aplica o preço do tratamento
  useEffect(() => {
    /*
    * Consultas abertas pelo módulo de alinhadores
    * não herdam valor financeiro automaticamente.
    */
    if (initialData?.aligner_treatment_id) {
      return;
    }

    if (!selectedTreatment) {
      return;
    }

    if (selectedTreatmentPrice == null) {
      return;
    }

    if (!amountTouched) {
      setAmount(
        String(selectedTreatmentPrice.toFixed(2))
      );
    }
  }, [
    selectedTreatment,
    selectedTreatmentPrice,
    amountTouched,
    initialData?.aligner_treatment_id,
  ]);

  const handleSave = async (confirmationToken = null) => {
    if (!isUnregistered && !patientId) {
      toast?.error('Selecione um paciente.');
      return;
    }
    if (isUnregistered && !patientName) {
      toast?.error('Informe o nome do paciente.');
      return;
    }
    if (!isYMD(appointmentDate)) {
      toast?.error('Informe a data (AAAA-MM-DD).');
      return;
    }
    if (!isHHMM(startTime)) {
      toast?.error('Informe o horário inicial (HH:mm).');
      return;
    }
    if (endTime && !isHHMM(endTime)) {
      toast?.error('Horário final inválido (HH:mm).');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      if (confirmationToken) {
        headers['X-Appointment-Conflict-Confirmation'] = confirmationToken;
      }

      const payload = {
        patient_id: isUnregistered ? null : patientId,
        patient_name: patientName || undefined,
        patient_phone: isUnregistered ? patientPhone : undefined,
        appointment_date: appointmentDate,
        start_time: startTime,
        end_time: endTime || undefined,
        procedure:
          procedure ||
          (selectedTreatment?.procedure ||
            selectedTreatment?.name ||
            selectedTreatment?.title) ||
          'Consulta',
        status: status || 'Agendado',
        notes: notes || '',
        professional_id: professionalId || undefined,
        professional_name: professionalName || undefined,
        booking_source: initialData?.booking_source || 'manual',        
        treatment_id:
          treatmentId || undefined,

        amount: (() => {
          const parsed = parseFloat(String(amount || '').replace(',', '.'));
          return Number.isFinite(parsed) ? parsed : 0;
        })(),
        expected_amount: (() => {
          const parsed = parseFloat(String(amount || '').replace(',', '.'));
          return Number.isFinite(parsed) ? parsed : 0;
        })(),
        push_to_finance:
          !initialData?.aligner_treatment_id && pushToFinance,

        aligner_treatment_id:
          initialData?.aligner_treatment_id ||
          undefined,

        reminders: [],
      };

      let resp;
      if (initialData?.id) {
        resp = await axios.put(`${API_URL}/appointments/${initialData.id}`, payload, { headers });
      } else {
        resp = await axios.post(`${API_URL}/appointments`, payload, { headers });
      }
      const saved = resp.data?.item || resp.data || payload;
      setConflictInfo(null);

      // A previsão financeira é persistida pelo backend junto ao agendamento.
      // A fatura permanece no fluxo próprio do módulo Financeiro.

      window.dispatchEvent(new CustomEvent('appointments:changed'));
      toast?.success(initialData?.id ? 'Agendamento atualizado!' : 'Agendamento criado!');
      onSaved && onSaved({ ...saved, id: initialData?.id || saved.id });
      onClose && onClose();
    } catch (e) {
      console.error('Erro ao salvar agendamento:', e);

      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === 'APPOINTMENT_CONFLICT') {
        setConflictInfo(detail);
        return;
      }

      const message =
        detail?.message ||
        (typeof detail === 'string' ? detail : null) ||
        e?.response?.data?.message ||
        'Erro ao salvar agendamento';

      window.alert(message);

      onClose && onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onChangeCapture={() => setIsDirty(true)}
      onClick={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="text-lg font-semibold text-gray-900">
            {initialData?.id ? 'Editar Agendamento' : 'Novo Agendamento'}
          </h3>
          <button type="button" onClick={requestClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          {/* Paciente */}
          {!currentPatient ? (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Paciente</label>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isUnregisteredCheck"
                    checked={isUnregistered}
                    onChange={(e) => {
                      setIsUnregistered(e.target.checked);
                      setPatientId('');
                      setPatientName('');
                      setPatientPhone('');
                      setTreatmentId('');
                      setAmount('');
                      setAmountTouched(false);
                    }}
                  />
                  <label htmlFor="isUnregisteredCheck" className="text-sm text-gray-600">
                    Paciente Avulso (Não cadastrado)
                  </label>
                </div>
              </div>
              
              {!isUnregistered ? (
                <>
                  <input
                    type="text"
                    placeholder="Buscar por nome, e-mail ou telefone…"
                    className="w-full border rounded-lg px-3 py-2 text-sm mb-2"
                    value={patientSearch}
                    onChange={(e) => setPatientSearch(e.target.value)}
                  />
                  <select
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    value={patientId}
                    onChange={(e) => onSelectPatient(e.target.value)}
                  >
                    <option value="">Selecione…</option>
                    {filteredPatients.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.full_name} {p.phone ? `— ${p.phone}` : ''} {p.email ? `— ${p.email}` : ''}
                      </option>
                    ))}
                  </select>
                </>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="Nome completo do paciente"
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    value={patientName}
                    onChange={(e) => setPatientName(e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="Telefone (opcional)"
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    value={patientPhone}
                    onChange={(e) => setPatientPhone(e.target.value)}
                  />
                </div>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Paciente</label>
              <input
                type="text"
                className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
                value={patientName}
                disabled
                readOnly
              />
            </div>
          )}

          {/* Data e horários */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
              <input
                type="date"
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={appointmentDate}
                onChange={(e) => setAppointmentDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Início</label>
              <input
                type="time"
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={startTime}
                onChange={(e) => handleStartTimeChange(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fim (opcional)</label>
              <input
                type="time"
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </div>

          {/* Procedimento e Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Procedimento</label>
              <input
                type="text"
                className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="Ex.: Consulta, Avaliação, Limpeza…"
                value={procedure}
                onChange={(e) => setProcedure(e.target.value)}
              />
            </div>
            <div>
              <label className="block text.sm font-medium text-gray-700 mb-1">Status</label>
              <select
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Tratamento + valor */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tratamento (opcional)</label>
              <select
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={treatmentId}
                onChange={(e) => {
                  setTreatmentId(e.target.value);
                  setAmountTouched(false);
                }}
              >
                <option value="">— Nenhum —</option>
                {availableTreatments.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.procedure || t.name || t.title || 'Tratamento'}
                  </option>
                ))}
              </select>
              {selectedTreatmentPrice != null && (
                <div className="text-xs text-gray-500 mt-1">
                  Valor padrão do tratamento: <strong>R$ {selectedTreatmentPrice.toFixed(2)}</strong>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valor do atendimento</label>
              <input
                type="text"
                inputMode="decimal"
                placeholder={selectedTreatmentPrice != null ? `Ex.: ${selectedTreatmentPrice.toFixed(2)}` : 'Ex.: 200,00'}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={amount}
                onChange={(e) => { setAmount(e.target.value); setAmountTouched(true); }}
              />
              {initialData?.aligner_treatment_id ? (
                <div className="mt-2 rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-xs text-purple-800">
                  Este agendamento pertence ao acompanhamento de
                  alinhadores e não será lançado automaticamente no
                  Financeiro.
                </div>
              ) : (
                <div className="mt-2 flex items-center">
                  <input
                    id="pushFinance"
                    type="checkbox"
                    className="mr-2"
                    checked={pushToFinance}
                    onChange={(e) =>
                      setPushToFinance(e.target.checked)
                    }
                  />

                  <label
                    htmlFor="pushFinance"
                    className="text-sm text-gray-700"
                  >
                    Incluir na previsão financeira
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Profissional */}
          {activeProfessionals.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Profissional
              </label>
              <select
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={professionalId}
                onChange={(e) => {
                  const prof = activeProfessionals.find((p) => p.id === e.target.value);
                  setProfessionalId(prof?.id || '');
                  setProfessionalName(prof?.name || '');
                }}
              >
                <option value="">Selecione o profissional</option>
                {activeProfessionals.map((prof) => (
                  <option key={prof.id} value={prof.id}>
                    {prof.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Observações */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
            <textarea
              className="w-full border rounded-lg px-3 py-2 text-sm"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Informações adicionais…"
            />
          </div>

        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button
            className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
            onClick={requestClose}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            onClick={() => handleSave()}
            disabled={saving}
          >
            {saving ? 'Salvando...' : (initialData?.id ? 'Salvar alterações' : 'Criar agendamento')}
          </button>
        </div>
      </div>
      <AppointmentConflictModal
        conflict={conflictInfo}
        saving={saving}
        onCancel={() => setConflictInfo(null)}
        onConfirm={() => handleSave(conflictInfo?.confirmation_token)}
      />
    </div>
  );
}
